/**
 * When an item page opens and one of its Linear links has not been synced in
 * a while (linearLinkIsStale), re-read the issue once and bring its mapped
 * fields over — so the description and Priority the page shows are Linear's
 * current ones without anyone pressing Sync. A sync that fails leaves the link
 * stale and is not retried until the page is opened again.
 */

import { useEffect, useRef } from 'react';
import { useUserStore } from '@/stores/prioritization/userStore';
import { linearLinkIsStale, syncLinkedLinearIssue } from '@/lib/linearItemSync';
import { syncLinkedJiraIssue } from '@/lib/jiraItemSync';
import type { ItemExternalLink } from './useItemExternalLinks';

export function useLinearAutoSync(
  workspaceId: string | null | undefined,
  itemId: string,
  links: ItemExternalLink[] | null,
): void {
  const userId = useUserStore((s) => s.userId);
  const started = useRef<string | null>(null);

  useEffect(() => {
    if (!workspaceId || !links) return;
    // Jira links re-read on the same clock, so story points the page shows are Jira's current ones.
    const stale = links.filter((l) => (l.connectorType === 'linear' || l.connectorType === 'jira') && linearLinkIsStale(l.lastSyncedAt));
    if (stale.length === 0) return;
    const key = `${itemId}:${stale.map((l) => l.externalId).join(',')}`;
    if (started.current === key) return;
    started.current = key;
    for (const link of stale) {
      if (link.connectorType === 'jira') {
        void syncLinkedJiraIssue({ workspaceId, itemId, issueKey: link.externalId, issueUrl: link.externalUrl, userId, reason: 'auto' });
      } else {
        void syncLinkedLinearIssue({ workspaceId, itemId, issueId: link.externalId, userId, reason: 'auto' });
      }
    }
  }, [workspaceId, itemId, links, userId]);
}
